import React, { useState } from "react";
import {
    Box,
    Card,
    TextField,
    Typography,
    Button,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from "@mui/material";
import { 
    Visibility, 
    VisibilityOff, 
    Email, 
    Lock, 
    Person, 
    Phone 
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const RegisterAdmin = () => {
    const [formData, setFormData] = useState({
        full_name: "",
        email: "",
        contact_no: "",
        password: "",
        confirm_password: "",
        role: "",
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const roles = [
        { value: "super_admin", label: "Super Admin" },
        { value: "admin", label: "Admin" },
        { value: "operation", label: "Operation" },
    ];

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone: string) => {
        const phoneRegex = /^\d{10}$/;
        return phoneRegex.test(phone);
    };

    const validatePassword = (password: string) => {
        // At least 8 characters, 1 uppercase, 1 lowercase, 1 number, 1 special character
        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        return passwordRegex.test(password);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
        setErrorMsg("");
        setSuccessMsg("");
    };

    const handleRoleChange = (e: any) => {
        setFormData((prev) => ({
            ...prev,
            role: e.target.value,
        }));
        setErrorMsg("");
        setSuccessMsg("");
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        // Frontend validation
        if (!formData.full_name.trim()) {
            setErrorMsg("Full name is required");
            setLoading(false);
            return;
        }

        if (!formData.email.trim()) {
            setErrorMsg("Email is required");
            setLoading(false);
            return;
        }

        if (!validateEmail(formData.email)) {
            setErrorMsg("Please enter a valid email address");
            setLoading(false);
            return;
        }

        if (!formData.contact_no.trim()) {
            setErrorMsg("Contact number is required");
            setLoading(false);
            return;
        }

        if (!validatePhone(formData.contact_no)) {
            setErrorMsg("Please enter a valid 10-digit contact number");
            setLoading(false);
            return;
        }

        if (!formData.password.trim()) {
            setErrorMsg("Password is required");
            setLoading(false);
            return;
        }

        if (!validatePassword(formData.password)) {
            setErrorMsg(
                "Password must be at least 8 characters with 1 uppercase, 1 lowercase, 1 number, and 1 special character"
            );
            setLoading(false);
            return;
        }

        if (!formData.confirm_password.trim()) {
            setErrorMsg("Please confirm your password");
            setLoading(false);
            return;
        }

        if (formData.password !== formData.confirm_password) {
            setErrorMsg("Passwords do not match");
            setLoading(false);
            return;
        }

        if (!formData.role) {
            setErrorMsg("Please select a role");
            setLoading(false);
            return;
        }

        const registerURL = `${process.env.REACT_APP_SERVER_ENDPOINT}/admin/register`;
        const authkey = "7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3";

        const requestBody = {
            full_name: formData.full_name.trim(),
            email: formData.email.trim(),
            contact_no: formData.contact_no.trim(),
            password: formData.password,
            confirm_password: formData.confirm_password,
            role: formData.role,
            status: 1,
        };

        try {
            const response = await fetch(registerURL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Accept": "application/json",
                    "authkey": authkey,
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                if (response.status === 404) {
                    throw new Error("404 Not Found: The API endpoint was not found.");
                } else if (response.status === 403) {
                    throw new Error("403 Forbidden: Invalid authkey or permissions.");
                } else if (response.status === 400) {
                    throw new Error("400 Bad Request: Invalid data provided.");
                } else if (response.status === 409) {
                    throw new Error("409 Conflict: Email or contact number already exists.");
                } else {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            }

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg(
                    result.message || "Registration successful! Redirecting to login..."
                );
                
                // Clear form
                setFormData({
                    full_name: "",
                    email: "",
                    contact_no: "",
                    password: "",
                    confirm_password: "",
                    role: "",
                });

                setTimeout(() => {
                    setSuccessMsg("");
                    navigate("/adminLogin");
                }, 2500);
            } else {
                setErrorMsg(result.message || "Registration failed. Please try again.");
                setSuccessMsg("");
            }
        } catch (error: any) {
            console.error("Registration error:", error);
            if (error.name === "TypeError" && error.message.includes("fetch")) {
                setErrorMsg(
                    "Network error: Unable to connect to server. Please check your connection."
                );
            } else if (error.message.includes("HTTP error")) {
                setErrorMsg(`Server error: ${error.message}`);
            } else {
                setErrorMsg(`Registration failed: ${error.message}`);
            }
            setSuccessMsg("");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#f5f5f5",
                padding: 2,
            }}
        >
            <Card
                sx={{
                    width: 500,
                    padding: 4,
                    boxShadow: 4,
                    borderRadius: 2,
                    backgroundColor: "#ffffff",
                }}
            >
                <Typography
                    variant="h4"
                    align="center"
                    gutterBottom
                    sx={{
                        color: "#1976d2",
                        fontWeight: "bold",
                        mb: 3,
                    }}
                >
                    Admin Registration
                </Typography>

                {successMsg && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMsg}
                    </Alert>
                )}

                {errorMsg && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMsg}
                    </Alert>
                )}

                <form onSubmit={handleRegister}>
                    <TextField
                        label="Full Name"
                        name="full_name"
                        type="text"
                        fullWidth
                        value={formData.full_name}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Person color="action" />
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter your full name"
                    />

                    <TextField
                        label="Email Address"
                        name="email"
                        type="email"
                        fullWidth
                        value={formData.email}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Email color="action" />
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter your email address"
                    />

                    <TextField
                        label="Contact Number"
                        name="contact_no"
                        type="text"
                        fullWidth
                        value={formData.contact_no}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Phone color="action" />
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter 10-digit contact number"
                        inputProps={{ maxLength: 10 }}
                    />

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="role-select-label">Role</InputLabel>
                        <Select
                            labelId="role-select-label"
                            id="role-select"
                            value={formData.role}
                            label="Role"
                            onChange={handleRoleChange}
                            disabled={loading}
                        >
                            {roles.map((role) => (
                                <MenuItem key={role.value} value={role.value}>
                                    {role.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        fullWidth
                        value={formData.password}
                        onChange={handleInputChange}
                        sx={{ mb: 2 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Enter your password"
                    />

                    <TextField
                        label="Confirm Password"
                        name="confirm_password"
                        type={showConfirmPassword ? "text" : "password"}
                        fullWidth
                        value={formData.confirm_password}
                        onChange={handleInputChange}
                        sx={{ mb: 3 }}
                        disabled={loading}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() =>
                                            setShowConfirmPassword(!showConfirmPassword)
                                        }
                                        edge="end"
                                    >
                                        {showConfirmPassword ? (
                                            <VisibilityOff />
                                        ) : (
                                            <Visibility />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Confirm your password"
                    />

                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={loading}
                        sx={{
                            textTransform: "none",
                            fontWeight: "bold",
                            py: 1.5,
                            fontSize: "1.1rem",
                        }}
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                Registering...
                            </>
                        ) : (
                            "Register"
                        )}
                    </Button>

                    <Button
                        onClick={() => navigate("/adminLogin")}
                        variant="text"
                        fullWidth
                        sx={{
                            mt: 2,
                            textTransform: "none",
                            color: "#1976d2",
                        }}
                    >
                        Already have an account? Login here
                    </Button>
                </form>
            </Card>
        </Box>
    );
};

export default RegisterAdmin;